import React, { useCallback, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import { capitalize, debounce, isEmpty } from "lodash"
import moment from "moment"

import Breadcrumbs from "components/Common/Breadcrumb"
import SearchBox from "components/Common/SearchBox"
import PastLastPage, { isPastLastPage } from "components/Common/PastLastPage"
import listColumns from "./listColumns"
import FilterList from "./FilterList"
import { getClubs, getClubsLocations, refreshClubs } from "./actions"
import { anyTranslating, useTranslationRefresh } from "components/Common/localizedText"
import "../../../assets/scss/custom/pages/_clubAccounts.scss"
import {
  DEFAULT_ADMIN_LIST_OPTIONS,
  applyAdminListFilterChange,
  applyAdminListSearchChange,
  normalizeAdminListOptions,
  shouldFetchAdminList,
} from "helpers/adminListQueryPolicy"

const SECONDARY_FILTERS = new Set(["fromDate", "toDate"])

const filterLabels = {
  location: "Location",
  sportType: "Sport",
  status: "Status",
  bookingEnabled: "Booking",
  fromDate: "Created from",
  toDate: "Created to",
}

const formatFilterValue = ([key, value]) => {
  if (key === "location") {
    const [city, country] = String(value).split(", ")
    return `Location: ${[city !== "null" ? city : "", country]
      .filter(Boolean)
      .join(", ")}`
  }
  if (key === "bookingEnabled") {
    return `Booking: ${value === "yes" ? "Enabled" : "Disabled"}`
  }
  if (SECONDARY_FILTERS.has(key)) {
    return `${filterLabels[key]}: ${moment(value).format("D MMM YYYY")}`
  }

  return `${filterLabels[key] || key}: ${capitalize(String(value))}`
}

export const Clubs = props => {
  const { clubs, getClubs, getClubsLocations, clubsLocations, refreshClubs } = props
  const initialContext = props.location?.state?.clubListContext || {}
  const initialOptions = normalizeAdminListOptions(initialContext.options, DEFAULT_ADMIN_LIST_OPTIONS)
  const [advancedFilters, setAdvancedFilters] = useState(Boolean(initialContext.advancedFilters))
  const [filters, setFilters] = useState(initialContext.filters || {})
  const [searchInputText, setSearchInputText] = useState(initialContext.searchInputText || "")
  const [activeSearchText, setActiveSearchText] = useState(initialContext.activeSearchText || "")
  const [options, setOptions] = useState(initialOptions)
  const clubListFilters = useMemo(
    () =>
      activeSearchText ? { ...filters, search: activeSearchText } : filters,
    [activeSearchText, filters]
  )
  const activeFilterEntries = useMemo(() => Object.entries(filters), [filters])
  const secondaryFilterCount = activeFilterEntries.filter(([key]) =>
    SECONDARY_FILTERS.has(key)
  ).length
  const totalResults = Number(clubs.totalResults) || 0
  const currentPage = Number(clubs.page) || options.page
  const pageSize = Number(clubs.limit) || options.limit
  const firstResult = totalResults ? (currentPage - 1) * pageSize + 1 : 0
  const lastResult = Math.min(currentPage * pageSize, totalResults)
  // The profile breadcrumb restores the list's page, which a Block (or another admin's change) can leave past the end.
  // The answer held is judged on its own page: the store keeps it until the next one arrives, so "Go to first page",
  // a filter or Rows keep the message instead of "No clubs match" with "31–30 of 30", and nothing is past the end
  // before the first answer.
  const pastEnd = isPastLastPage(Number(clubs.page), clubs)
  const pageOptions = {
    page: currentPage,
    sizePerPage: pageSize,
    totalSize: totalResults,
    custom: true,
    hidePageListOnlyOnePage: true,
    hideSizePerPage: true,
  }

  useEffect(() => {
    if (!shouldFetchAdminList(clubListFilters)) return

    getClubs(clubListFilters, options)
  }, [clubListFilters, getClubs, options])

  useEffect(() => {
    getClubsLocations(filters)
  }, [filters, getClubsLocations])

  // While a club's texts translate (ADR 0013), the list reads itself again until their markers settle; a read that fails keeps it.
  const translating = (clubs.results || []).some(club =>
    anyTranslating(club.translationAttention)
  )
  useTranslationRefresh(
    translating,
    () => refreshClubs?.(clubListFilters, options),
    JSON.stringify([clubListFilters, options])
  )

  const handleFiltersChange = useCallback(
    nextFilters => {
      const nextState = applyAdminListFilterChange({
        nextFilters,
        options,
      })

      setFilters(nextState.filters)
      setOptions(nextState.options)
    },
    [options]
  )

  const applySearchQuery = useCallback(
    value => {
      const nextState = applyAdminListSearchChange({
        filters: clubListFilters,
        options,
        searchText: value,
      })

      if (!nextState.shouldUpdate) return

      setActiveSearchText(nextState.searchText)
      setSearchInputText(nextState.inputText)
      setOptions(nextState.options)
    },
    [clubListFilters, options]
  )

  const handleChangeWithDebounce = useMemo(
    () => debounce(applySearchQuery, 700),
    [applySearchQuery]
  )

  useEffect(
    () => () => handleChangeWithDebounce.cancel(),
    [handleChangeWithDebounce]
  )

  const handleSearchInputChange = useCallback(
    value => {
      setSearchInputText(value || "")
      handleChangeWithDebounce(value || "")
    },
    [handleChangeWithDebounce]
  )

  // the „×”: the whole list at once, without the typing delay
  const clearSearch = useCallback(() => {
    handleChangeWithDebounce.cancel()
    applySearchQuery("")
  }, [handleChangeWithDebounce, applySearchQuery])

  const handleTableChange = (type, data) => {
    if (type === "pagination") {
      setOptions({ ...options, page: data.page })
      return
    }

    if (type === "search") {
      const value = data.searchText || ""
      setSearchInputText(value)
      handleChangeWithDebounce(value)
      return
    }

    if (type === "sort") {
      const sortBy = `${data.sortField}:${data.sortOrder}`
      if (sortBy !== options.sortBy) {
        setOptions({
          ...normalizeAdminListOptions(options),
          page: 1,
          sortBy,
        })
      }
    }
  }

  const resetFilters = () => {
    if (!isEmpty(filters) || activeSearchText || searchInputText) {
      handleChangeWithDebounce.cancel()
      setFilters({})
      setActiveSearchText("")
      setSearchInputText("")
      setOptions({ ...options, page: 1 })
    }
  }

  const removeFilter = key => {
    const nextFilters = { ...filters }
    delete nextFilters[key]
    handleFiltersChange(nextFilters)
  }

  const handlePageSizeChange = event => {
    setOptions({
      ...options,
      page: 1,
      limit: Number(event.target.value),
    })
  }

  const clubListContext = { filters, searchInputText, activeSearchText, options, advancedFilters }

  const rowClasses = row => (row?.status === "blocked" ? "disabledRow" : "")

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            path="/accounts/clubs"
            title="User Accounts"
            breadcrumbItem="Clubs"
            displayTotal={clubs.totalResults}
          />
          <Row>
            <Col lg="12">
              <Card className="clubs-directory-card">
                <CardBody className="p-0">
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        data={clubs.results || []}
                        columns={listColumns({ clubListContext, clubListSearch: props.location?.search || '' })}
                        bootstrap4
                        search
                      >
                        {toolkitProps => (
                          <React.Fragment>
                            <div className="club-directory-toolbar">
                              <div className="club-directory-heading">
                                <div>
                                  <h4 className="mb-1">Club directory</h4>
                                  <p className="text-muted mb-0">
                                    Find a venue and open its complete club
                                    profile.
                                  </p>
                                </div>
                                <Link
                                  to={{ pathname: '/accounts/clubs/create', state: { clubListContext, clubListSearch: props.location?.search || '' } }}
                                  onClick={() => props.history.replace({ ...props.location, state: { ...props.location?.state, clubListContext } })}
                                  className="btn btn-success"
                                >
                                  <i className="mdi mdi-plus mr-1" />
                                  Create club
                                </Link>
                              </div>

                              <div className="club-search-row">
                                <SearchBox
                                  look="pill"
                                  className="club-search-box"
                                  onValueChange={handleSearchInputChange}
                                  onClear={clearSearch}
                                  placeholder="Search club, business name, ID, owner, email or phone"
                                  value={searchInputText}
                                  aria-label="Search clubs"
                                />
                                <Button
                                  type="button"
                                  color="light"
                                  className="club-more-filters"
                                  onClick={() =>
                                    setAdvancedFilters(value => !value)
                                  }
                                  aria-expanded={advancedFilters}
                                >
                                  <i className="bx bx-slider-alt mr-1" />
                                  More filters
                                  {secondaryFilterCount > 0 && (
                                    <span className="badge badge-primary ml-2">
                                      {secondaryFilterCount}
                                    </span>
                                  )}
                                  <i
                                    className={`bx bx-chevron-${
                                      advancedFilters ? "up" : "down"
                                    } ml-1`}
                                  />
                                </Button>
                              </div>

                              <FilterList
                                advancedFilters={advancedFilters}
                                locations={clubsLocations}
                                filters={filters}
                                setFilters={handleFiltersChange}
                              />

                              {activeFilterEntries.length > 0 && (
                                <div className="club-active-filters">
                                  <span className="text-muted font-size-12">
                                    Applied filters
                                  </span>
                                  {activeFilterEntries.map(entry => (
                                    <button
                                      type="button"
                                      className="club-filter-chip"
                                      key={entry[0]}
                                      onClick={() => removeFilter(entry[0])}
                                      aria-label={`Remove ${formatFilterValue(
                                        entry
                                      )} filter`}
                                    >
                                      {formatFilterValue(entry)}
                                      <i
                                        className="bx bx-x"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm club-clear-filters"
                                    onClick={resetFilters}
                                  >
                                    Clear all
                                  </button>
                                </div>
                              )}
                            </div>

                            <Row className="mx-0">
                              <Col xl="12" className="px-0">
                                <div className="table-responsive club-directory-table">
                                  <BootstrapTable
                                    responsive
                                    remote
                                    bordered={false}
                                    striped={false}
                                    rowClasses={rowClasses}
                                    classes="table align-middle table-hover"
                                    headerWrapperClasses="thead-light"
                                    {...toolkitProps.baseProps}
                                    onTableChange={handleTableChange}
                                    {...paginationTableProps}
                                    noDataIndication={pastEnd
                                      ? () => <PastLastPage onFirstPage={() => setOptions({ ...options, page: 1 })} />
                                      : "No clubs match the current filters."}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <div className="club-directory-footer">
                              <div className="club-result-summary">
                                {!pastEnd && <>
                                  Showing{" "}
                                  <strong>
                                    {firstResult}–{lastResult}
                                  </strong>{" "}
                                  of{" "}
                                </>}
                                <strong>{totalResults.toLocaleString()}</strong>{" "}
                                clubs
                              </div>
                              {!pastEnd && <div className="pagination pagination-rounded inner-custom-pagination mb-0">
                                <PaginationListStandalone
                                  {...paginationProps}
                                />
                              </div>}
                              <label className="club-page-size mb-0">
                                <span>Rows</span>
                                <select
                                  className="custom-select custom-select-sm"
                                  value={options.limit}
                                  onChange={handlePageSizeChange}
                                >
                                  {[15, 30, 50].map(size => (
                                    <option value={size} key={size}>
                                      {size}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = ({ clubs: { clubs, clubsLocations } = {} }) => ({
  clubs,
  clubsLocations,
})

const mapDispatchToProps = dispatch => ({
  getClubs: (filters, options) => dispatch(getClubs(filters, options)),
  getClubsLocations: filters => dispatch(getClubsLocations(filters)),
  refreshClubs: (filters, options) => dispatch(refreshClubs(filters, options)),
})

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Clubs))
